import { describeDomains } from "../operation-meta.js";
import { personIDQueryArgsSchema, personYearQueryArgsSchema, yearQueryArgsSchema, } from "../projects-input-schemas.js";
import { ForbiddenError } from "../../errors.js";
import { assertAdminDataWriteAccess, toZeroDate } from "../projects-helpers.js";
export function createHrQueries({ defineProjectQuery, projectZql, }) {
    return {
        publicHolidays: {
            list: defineProjectQuery(yearQueryArgsSchema, ({ args, ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.publicHolidays.where("id", "__unauthorized__");
                }
                let query = projectZql.publicHolidays;
                if (args.year) {
                    query = query
                        .where("date", ">=", Date.UTC(args.year, 0, 1))
                        .where("date", "<=", Date.UTC(args.year, 11, 31));
                }
                return query.orderBy("date", "asc");
            }),
        },
        employeeAbsences: {
            byPerson: defineProjectQuery(personYearQueryArgsSchema, ({ args, ctx, }) => {
                if (ctx.role !== "admin" &&
                    ctx.role !== "employee" &&
                    ctx.personID !== args.personID) {
                    return projectZql.employeeAbsences.where("id", "__unauthorized__");
                }
                let query = projectZql.employeeAbsences.where("personID", args.personID);
                if (args.year) {
                    query = query
                        .where("startDate", ">=", Date.UTC(args.year, 0, 1))
                        .where("startDate", "<=", Date.UTC(args.year, 11, 31));
                }
                return query.orderBy("startDate", "desc");
            }),
            all: defineProjectQuery(yearQueryArgsSchema, ({ args, ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.employeeAbsences.where("id", "__unauthorized__");
                }
                let query = projectZql.employeeAbsences.related("person");
                if (args.year) {
                    query = query
                        .where("startDate", ">=", Date.UTC(args.year, 0, 1))
                        .where("startDate", "<=", Date.UTC(args.year, 11, 31));
                }
                return query.orderBy("startDate", "desc");
            }),
        },
        employeeEntitlements: {
            byPerson: defineProjectQuery(personIDQueryArgsSchema, ({ args, ctx, }) => {
                if (ctx.role !== "admin" &&
                    ctx.role !== "employee" &&
                    ctx.personID !== args.personID) {
                    return projectZql.employeeEntitlements.where("id", "__unauthorized__");
                }
                return projectZql.employeeEntitlements
                    .where("personID", args.personID)
                    .orderBy("year", "desc");
            }),
        },
        employmentPeriods: {
            byPerson: defineProjectQuery(personIDQueryArgsSchema, ({ args, ctx, }) => {
                if (ctx.role !== "admin" &&
                    ctx.role !== "employee" &&
                    ctx.personID !== args.personID) {
                    return projectZql.employmentPeriods.where("id", "__unauthorized__");
                }
                return projectZql.employmentPeriods
                    .where("personID", args.personID)
                    .orderBy("validFrom", "desc");
            }),
        },
        overtimePayouts: {
            byPerson: defineProjectQuery(personYearQueryArgsSchema, ({ args, ctx, }) => {
                // Overtime is visible to the person themselves and to admins.
                if (ctx.role !== "admin" && ctx.personID !== args.personID) {
                    return projectZql.overtimePayouts.where("id", "__unauthorized__");
                }
                let query = projectZql.overtimePayouts.where("personID", args.personID);
                if (args.year) {
                    query = query
                        .where("date", ">=", Date.UTC(args.year, 0, 1))
                        .where("date", "<=", Date.UTC(args.year, 11, 31));
                }
                return query.orderBy("date", "desc");
            }),
        },
    };
}
/** Overtime payouts change someone's overtime balance: admins only. */
function assertOvertimeAdmin(ctx) {
    if (ctx.role !== "admin") {
        throw new ForbiddenError("Only admins can manage overtime payouts");
    }
}
export function createHrMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createPublicHolidayInputSchema, updatePublicHolidayInputSchema, publicHolidayIDInputSchema, createEmployeeAbsenceInputSchema, updateEmployeeAbsenceInputSchema, employeeAbsenceIDInputSchema, upsertEmployeeEntitlementInputSchema, createEmploymentPeriodInputSchema, updateEmploymentPeriodInputSchema, employmentPeriodIDInputSchema, createOvertimePayoutInputSchema, overtimePayoutIDInputSchema, } = schemas;
    return {
        publicHolidays: {
            create: defineProjectMutator(createPublicHolidayInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.publicHolidays.insert({
                    id: args.id,
                    name: args.name,
                    date: toZeroDate(args.date),
                });
            }),
            update: defineProjectMutator(updatePublicHolidayInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.publicHolidays.update({
                    ...args,
                    date: toZeroDate(args.date),
                });
            }),
            delete: defineProjectMutator(publicHolidayIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.publicHolidays.delete({ id: args.id });
            }),
        },
        employeeAbsences: {
            create: defineProjectMutator(createEmployeeAbsenceInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.employeeAbsences.insert({
                    id: args.id,
                    personID: args.personID,
                    absenceType: args.absenceType,
                    startDate: toZeroDate(args.startDate),
                    endDate: toZeroDate(args.endDate),
                    notes: args.notes ?? null,
                });
            }),
            update: defineProjectMutator(updateEmployeeAbsenceInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.employeeAbsences.update({
                    ...args,
                    startDate: toZeroDate(args.startDate),
                    endDate: toZeroDate(args.endDate),
                });
            }),
            delete: defineProjectMutator(employeeAbsenceIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.employeeAbsences.delete({ id: args.id });
            }),
        },
        employeeEntitlements: {
            upsert: defineProjectMutator(upsertEmployeeEntitlementInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const existing = await tx.run(projectZql.employeeEntitlements
                    .where("personID", args.personID)
                    .where("year", args.year)
                    .one());
                const row = {
                    id: existing?.id ?? args.id ?? crypto.randomUUID(),
                    personID: args.personID,
                    year: args.year,
                    vacationDays: args.vacationDays ?? 0,
                    carryOverDays: args.carryOverDays ?? 0,
                    notes: args.notes ?? null,
                };
                if (existing) {
                    await tx.mutate.employeeEntitlements.update(row);
                }
                else {
                    await tx.mutate.employeeEntitlements.insert(row);
                }
            }),
        },
        employmentPeriods: {
            create: defineProjectMutator(createEmploymentPeriodInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                if (args.validFrom) {
                    const previousDay = new Date(args.validFrom);
                    previousDay.setUTCDate(previousDay.getUTCDate() - 1);
                    const previousDayValue = Date.UTC(previousDay.getUTCFullYear(), previousDay.getUTCMonth(), previousDay.getUTCDate());
                    const openPeriods = await tx.run(projectZql.employmentPeriods
                        .where("personID", args.personID)
                        .where("validTo", "IS", null));
                    for (const period of openPeriods ?? []) {
                        await tx.mutate.employmentPeriods.update({
                            id: period.id,
                            validTo: previousDayValue,
                        });
                    }
                }
                await tx.mutate.employmentPeriods.insert({
                    id: args.id,
                    personID: args.personID,
                    percentage: args.percentage,
                    validFrom: toZeroDate(args.validFrom),
                    validTo: toZeroDate(args.validTo),
                });
            }),
            update: defineProjectMutator(updateEmploymentPeriodInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.employmentPeriods.update({
                    ...args,
                    validFrom: toZeroDate(args.validFrom),
                    validTo: toZeroDate(args.validTo),
                });
            }),
            delete: defineProjectMutator(employmentPeriodIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.employmentPeriods.delete({ id: args.id });
            }),
        },
        overtimePayouts: {
            create: defineProjectMutator(createOvertimePayoutInputSchema, async ({ tx, ctx, args, }) => {
                assertOvertimeAdmin(ctx);
                await tx.mutate.overtimePayouts.insert({
                    id: args.id,
                    personID: args.personID,
                    date: toZeroDate(args.date),
                    hours: args.hours,
                    notes: args.notes ?? null,
                    createdBy: args.createdBy ?? null,
                });
            }),
            delete: defineProjectMutator(overtimePayoutIDInputSchema, async ({ tx, ctx, args, }) => {
                assertOvertimeAdmin(ctx);
                await tx.mutate.overtimePayouts.delete({ id: args.id });
            }),
        },
    };
}
export const hrDomainDescriptions = describeDomains({
    publicHolidays: "Public holidays used for working-time calculations",
    employeeAbsences: "Vacation, sickness and other absences of employees",
    employeeEntitlements: "Yearly vacation entitlements per employee",
    employmentPeriods: "Employment periods and workload percentage per employee",
    overtimePayouts: "Overtime hours paid out to employees (admin only)",
});
